import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ContentCopy, Check, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { PlayerMatch } from '../../models/PlayerMatch';
import { RoundEnd } from '../../models/RoundEnd';
import { Team } from '../../models/Team';
import { SwingBreakdown, SwingEvent, SwingRound } from '../../models/SwingBreakdown';
import TeamLogo from '../../components/TeamLogo';
import { Side, ctWonRound, playerName } from './matchStats';
import { CT_COLOR, T_COLOR, roundEndIcon } from './roundEndIcon';
import skullIcon from '../../content/killIndicators/icon_skull_default.svg';
import c4Icon from '../../content/weapons/c4.svg';
import styles from './MatchPage2.module.css';

interface Match2SwingProps {
    data: SwingBreakdown;
    rows: PlayerMatch[]; // this map's player rows, for display names
    team1: Team;
    team2: Team;
    team1Ids: string[];
    roundEnds?: { [round: number]: RoundEnd }; // win type per round, for the icons under the bars
    side: Side;
    selectedSteamId: string | null;
    onSelect: (steamId: string) => void;
}

type SideKey = 'ct' | 't';
type Cat = 'kill' | 'mateKill' | 'death' | 'mateDeath' | 'plant' | 'end' | 'other';
const CAT_ORDER: Cat[] = ['kill', 'mateKill', 'death', 'mateDeath', 'plant', 'end', 'other'];
const MUTED = '#8a9099'; // team kills and suicides: nobody's side gets the event

const fmtPct = (v: number, dp = 1): string => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(dp)}%`;
const fmtTick = (v: number): string => (v === 0 ? '0%' : `${v > 0 ? '+' : '−'}${Math.abs(v)}%`);
const tone = (v: number): string => (v > 0.05 ? styles.good : v < -0.05 ? styles.bad : styles.swingMuted);
const isDeath = (e: SwingEvent) => e.type === 'K' || e.type === 'TK' || e.type === 'SU';
const sideColor = (s: SideKey) => (s === 'ct' ? CT_COLOR : T_COLOR);
const otherSide = (s: SideKey): SideKey => (s === 'ct' ? 't' : 'ct');
// Smallest round scale that fits the biggest bar, so the bars use the height without odd tick values.
const niceMax = (m: number) => [10, 20, 40, 60, 80, 100, 120, 150].find(s => m <= s) ?? Math.ceil(m / 50) * 50;

const creditOf = (r: SwingRound, ix: number): number => r.credit.find(c => c[0] === ix)?.[1] ?? 0;
const deltaOf = (e: SwingEvent, ix: number): number => e.d.find(c => c[0] === ix)?.[1] ?? 0;
const sideOf = (r: SwingRound, ix: number): SideKey | null => (r.ct.includes(ix) ? 'ct' : r.t.includes(ix) ? 't' : null);

// A white SVG tinted through a CSS mask, the same way as the round-history strip.
const maskStyle = (icon: string, color: string): React.CSSProperties => ({
    backgroundColor: color,
    WebkitMask: `url(${icon}) center / contain no-repeat`,
    mask: `url(${icon}) center / contain no-repeat`,
});

// Win type of a round: the round-end row when it agrees with the swing's winner, else read off the end event.
const endReasonOf = (r: SwingRound, roundEnds?: { [round: number]: RoundEnd }): string => {
    const re = roundEnds?.[r.n];
    if (re && ctWonRound(re.reason) === r.ctWon) return re.reason;
    if (r.events.some(e => e.type.startsWith('E') && e.type.includes('d'))) return 'BombDefused';
    return r.ctWon ? 'CTsWin' : 'TerroristsWin';
};

// Icon and colour of an event marker: kills in the killer's side colour, the plant in T, the end in the winner's.
const markOf = (r: SwingRound, e: SwingEvent, reason: string): { icon: string; color: string } => {
    if (e.type === 'P') return { icon: c4Icon, color: T_COLOR };
    if (e.type.startsWith('E')) return { icon: roundEndIcon(reason), color: r.ctWon ? CT_COLOR : T_COLOR };
    if (e.type === 'K') {
        const victim = e.vi != null ? sideOf(r, e.vi) : null;
        const killer = (e.k != null ? sideOf(r, e.k) : null) ?? (victim ? otherSide(victim) : null);
        if (killer) return { icon: skullIcon, color: sideColor(killer) };
    }
    return { icon: skullIcon, color: MUTED };
};

// Which bucket an event's credit for player `me` belongs in (the ledger under the rounds).
const categorize = (e: SwingEvent, me: number, mates: number[]): Cat => {
    if (e.type === 'P') return 'plant';
    if (e.type.startsWith('E')) return 'end';
    if (e.type === 'K' && e.k === me) return 'kill';
    if (isDeath(e) && e.vi === me) return 'death';
    if (e.type === 'K' && e.k != null && mates.includes(e.k)) return 'mateKill';
    if (isDeath(e) && e.vi != null && mates.includes(e.vi)) return 'mateDeath';
    return 'other';
};

// One hover/focus tooltip for the whole tab, portalled to <body> like the kill matrix's so the
// horizontally scrolling charts can't clip it. Flips below the target near the top of the window.
interface TipApi {
    show: (el: HTMLElement, body: React.ReactNode) => void;
    hide: () => void;
}
const useTip = (): TipApi & { node: React.ReactNode } => {
    const [tip, setTip] = useState<{ left: number; top: number; above: boolean; body: React.ReactNode } | null>(null);
    useEffect(() => {
        if (!tip) return;
        const hide = () => setTip(null);
        window.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);
        return () => {
            window.removeEventListener('scroll', hide, true);
            window.removeEventListener('resize', hide);
        };
    }, [tip]);
    const show = useCallback((el: HTMLElement, body: React.ReactNode) => {
        const r = el.getBoundingClientRect();
        const above = r.top > 180;
        const half = Math.min(140, window.innerWidth / 2 - 8); // the tip is at most 280px wide
        const left = Math.min(Math.max(r.left + r.width / 2, half + 8), window.innerWidth - half - 8);
        setTip({ left, top: above ? r.top - 8 : r.bottom + 8, above, body });
    }, []);
    const hide = useCallback(() => setTip(null), []);
    const node = tip && createPortal(
        <div className={`${styles.swingTip} ${tip.above ? styles.matrixTipAbove : styles.matrixTipBelow}`}
            style={{ left: tip.left, top: tip.top }}>
            {tip.body}
        </div>,
        document.body,
    );
    return { show, hide, node };
};

// Per-round swing bars on a symmetric scale, with each round's win icon (in the winning side's colour) as the
// x axis and a dashed line where the player switched sides. Rounds outside the side filter stay, dimmed.
const RoundBars: React.FC<{
    rounds: SwingRound[];
    me: number;
    isOn: (r: SwingRound) => boolean;
    selected: number | null;
    onPick: (n: number) => void;
    reasonOf: (r: SwingRound) => string;
    tipFor: (r: SwingRound) => React.ReactNode;
    labelFor: (r: SwingRound) => string;
    tip: TipApi;
}> = ({ rounds, me, isOn, selected, onPick, reasonOf, tipFor, labelFor, tip }) => {
    const max = niceMax(Math.max(0, ...rounds.filter(isOn).map(r => Math.abs(creditOf(r, me)))));
    const ticks = [max, max / 2, 0, -max / 2, -max];
    const top = (v: number) => `${50 - (v / max) * 50}%`;
    return (
        <div className={styles.swingBarsScroll}>
            <div className={styles.swingBars} style={{ '--n': rounds.length } as React.CSSProperties}>
                <div className={styles.swingBarsAxis} aria-hidden>
                    {ticks.map(v => <span key={v} style={{ top: top(v) }}>{fmtTick(v)}</span>)}
                </div>
                <div className={styles.swingBarsPlot}>
                    <div className={styles.swingBarsGrid} aria-hidden>
                        {ticks.map(v => <span key={v} className={v === 0 ? styles.swingBarsZero : undefined} style={{ top: top(v) }} />)}
                    </div>
                    {rounds.map((r, i) => {
                        const on = isOn(r);
                        const v = creditOf(r, me);
                        const h = Math.min(50, (Math.abs(v) / max) * 50);
                        const was = i > 0 ? sideOf(rounds[i - 1], me) : null;
                        const now = sideOf(r, me);
                        const cls = [
                            styles.swingBarsCol,
                            r.n === selected ? styles.swingBarsColActive : '',
                            on ? '' : styles.swingBarsColOff,
                            was && now && was !== now ? styles.swingBarsColSwitch : '',
                        ].join(' ');
                        return (
                            <button key={r.n} type="button" className={cls} disabled={!on}
                                aria-pressed={r.n === selected} aria-label={labelFor(r)}
                                onClick={() => onPick(r.n)}
                                onMouseEnter={e => tip.show(e.currentTarget, tipFor(r))}
                                onMouseLeave={tip.hide}
                                onFocus={e => tip.show(e.currentTarget, tipFor(r))}
                                onBlur={tip.hide}>
                                <span className={styles.swingBarsArea}>
                                    {on && Math.abs(v) >= 0.05 && (
                                        <span className={`${styles.swingBarsFill} ${v >= 0 ? styles.swingBarsFillPos : styles.swingBarsFillNeg}`}
                                            style={v >= 0 ? { bottom: '50%', height: `${h}%` } : { top: '50%', height: `${h}%` }} />
                                    )}
                                </span>
                                <span className={styles.swingBarsIcon} style={maskStyle(roundEndIcon(reasonOf(r)), r.ctWon ? CT_COLOR : T_COLOR)} />
                                <span className={styles.swingBarsNo}>{r.n}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// One round as a tug of war: the line is the player's team's chance of winning the round after each event
// (evenly spaced, in order), the area under it in his side's colour and the rest in the enemy's. Every event
// sits on the line as an icon; the ones he got credit for are ringed in orange with his share next to them.
const RoundChart: React.FC<{
    round: SwingRound;
    me: number;
    mySide: SideKey;
    reason: string;
    hot: number | null;
    setHot: (j: number | null) => void;
    tip: TipApi;
    tipFor: (j: number) => React.ReactNode; // j = -1: the round start
    labelFor: (j: number) => string;
    decidedLabel: string;
}> = ({ round, me, mySide, reason, hot, setHot, tip, tipFor, labelFor, decidedLabel }) => {
    const gid = useId().replace(/:/g, '');
    const ev = round.events;
    if (ev.length === 0) return null;

    const wp = (wt: number) => (mySide === 'ct' ? 100 - wt : wt);
    const pts = [wp(ev[0].wtb), ...ev.map(e => wp(e.wta))]; // slot 0 = round start, slot j + 1 = event j
    const xs = pts.map((_, i) => (i / (pts.length - 1)) * 100);
    const ys = pts.map(p => 100 - p);
    const line = pts.map((_, i) => `${xs[i].toFixed(2)},${ys[i].toFixed(2)}`).join(' ');
    // Decided before the first frozen event, or (only the end says so) before the end itself.
    const last = ev[ev.length - 1];
    let decided = ev.findIndex(e => e.frozen);
    if (decided < 0 && last.type.startsWith('E') && last.type.includes('c')) decided = ev.length - 1;
    const enemy = otherSide(mySide);

    const hover = (j: number) => ({
        onMouseEnter: (e: React.MouseEvent<HTMLElement>) => { setHot(j); tip.show(e.currentTarget, tipFor(j)); },
        onFocus: (e: React.FocusEvent<HTMLElement>) => { setHot(j); tip.show(e.currentTarget, tipFor(j)); },
        onClick: (e: React.MouseEvent<HTMLElement>) => { setHot(j); tip.show(e.currentTarget, tipFor(j)); },
        onMouseLeave: () => { setHot(null); tip.hide(); },
        onBlur: () => { setHot(null); tip.hide(); },
    });

    return (
        <div className={styles.swingRcScroll}>
            <div className={styles.swingRcPlot} style={{ '--n': pts.length } as React.CSSProperties}>
                <div className={styles.swingRcInner}>
                    {[100, 75, 50, 25, 0].map(p => (
                        <span key={p} className={styles.swingRcYLbl} style={{ top: `${100 - p}%` }} aria-hidden>{p}%</span>
                    ))}
                    <svg className={styles.swingRcSvg} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
                        <defs>
                            <linearGradient id={`${gid}me`} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="100">
                                <stop offset="0" stopColor={sideColor(mySide)} stopOpacity="0.45" />
                                <stop offset="1" stopColor={sideColor(mySide)} stopOpacity="0.08" />
                            </linearGradient>
                            <linearGradient id={`${gid}them`} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="100">
                                <stop offset="0" stopColor={sideColor(enemy)} stopOpacity="0.08" />
                                <stop offset="1" stopColor={sideColor(enemy)} stopOpacity="0.45" />
                            </linearGradient>
                        </defs>
                        <polygon points={`${line} 100,0 0,0`} fill={`url(#${gid}them)`} />
                        <polygon points={`${line} 100,100 0,100`} fill={`url(#${gid}me)`} />
                        {[0, 25, 75, 100].map(p => <line key={p} x1="0" x2="100" y1={p} y2={p} className={styles.swingRcGrid} />)}
                        <line x1="0" x2="100" y1="50" y2="50" className={styles.swingRcHalf} />
                        <polyline points={line} className={styles.swingRcLine} />
                    </svg>
                    {decided >= 0 && (
                        <div className={styles.swingRcDecided} style={{ left: `${(xs[decided] + xs[decided + 1]) / 2}%` }}>
                            <span>{decidedLabel}</span>
                        </div>
                    )}
                    <button type="button" aria-label={labelFor(-1)} {...hover(-1)}
                        className={`${styles.swingRcMarker} ${styles.swingRcStart} ${hot === -1 ? styles.swingRcHot : ''}`}
                        style={{ left: '0%', top: `${ys[0]}%` }} />
                    {ev.map((e, j) => {
                        const slot = j + 1;
                        const mk = markOf(round, e, reason);
                        const v = deltaOf(e, me);
                        const mine = e.k === me || e.vi === me || Math.abs(v) >= 0.05;
                        // the share goes on the side the line didn't come from, flipped near the edges
                        const above = pts[slot] >= pts[slot - 1] ? pts[slot] <= 82 : pts[slot] < 18;
                        const cls = [
                            styles.swingRcMarker,
                            mine ? styles.swingRcMine : '',
                            e.type.startsWith('E') ? styles.swingRcEnd : '',
                            e.frozen ? styles.swingRcFrozen : '',
                            hot === j ? styles.swingRcHot : '',
                        ].join(' ');
                        return (
                            <React.Fragment key={j}>
                                <button type="button" aria-label={labelFor(j)} {...hover(j)} className={cls}
                                    style={{ left: `${xs[slot]}%`, top: `${ys[slot]}%`, '--c': mk.color } as React.CSSProperties}>
                                    <span className={styles.swingRcIcon} style={maskStyle(mk.icon, e.type.startsWith('E') ? '#161616' : mk.color)} />
                                </button>
                                {Math.abs(v) >= 1 && !e.frozen && (
                                    <span aria-hidden
                                        className={`${styles.swingRcChip} ${above ? styles.swingRcChipUp : styles.swingRcChipDown} ${v > 0 ? styles.swingRcChipGood : styles.swingRcChipBad}`}
                                        style={{ left: `${xs[slot]}%`, top: `${ys[slot]}%` }}>
                                        {fmtPct(v)}
                                    </span>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// Swing tab: the Rating 3.0 swing of one player, round by round and event by event — what each kill,
// death and plant did to his team's chance of winning the round, and his share of it.
const Match2Swing: React.FC<Match2SwingProps> = ({ data, rows, team1, team2, team1Ids, roundEnds, side, selectedSteamId, onSelect }) => {
    const { t } = useTranslation('pages');
    const [roundNo, setRoundNo] = useState<number | null>(null);
    const [hot, setHot] = useState<number | null>(null); // event hovered in the chart or the table
    const [copied, setCopied] = useState(false);
    const tip = useTip();

    const rowById = useMemo(() => new Map(rows.map(r => [r.steamId, r])), [rows]);
    const name = (ix: number | undefined): string => {
        if (ix == null || ix < 0) return '?';
        const p = data.players[ix];
        const row = p ? rowById.get(p.id) : undefined;
        return row ? playerName(row) : p?.name || p?.id.slice(-6) || '?';
    };

    // Team 1 = the team whose ids the match lists; anyone else (stand-ins) goes by who he shared a side with.
    const t1 = useMemo(() => {
        const set = new Set<number>();
        data.players.forEach((p, i) => { if (team1Ids.includes(p.id)) set.add(i); });
        for (const r of data.rounds) {
            for (const roster of [r.ct, r.t]) {
                if (roster.some(i => set.has(i))) roster.forEach(i => set.add(i));
            }
        }
        return set;
    }, [data, team1Ids]);

    // Team 1 : team 2 after each round, in the header's order.
    const scoreAfter = useMemo(() => {
        const m = new Map<number, [number, number]>();
        let a = 0, b = 0;
        for (const r of data.rounds) {
            if (r.ctWon === r.ct.some(i => t1.has(i))) a++; else b++;
            m.set(r.n, [a, b]);
        }
        return m;
    }, [data, t1]);

    const counted = useMemo(() => data.rounds.filter(r => !r.skipped), [data]);
    const inSide = (r: SwingRound, ix: number) => {
        const s = sideOf(r, ix);
        return s != null && (side === 'all' || s === side);
    };
    const swingOf = (ix: number): { pct: number | null; n: number } => {
        const rs = counted.filter(r => inSide(r, ix));
        if (rs.length === 0) return { pct: null, n: 0 };
        return { pct: rs.reduce((a, r) => a + creditOf(r, ix), 0) / rs.length, n: rs.length };
    };

    const order = data.players.map((_, i) => i).sort((a, b) => Number(t1.has(b)) - Number(t1.has(a)));
    const picked = data.players.findIndex(p => p.id === selectedSteamId);
    const me = picked >= 0 ? picked : order[0];
    const myTeam = t1.has(me) ? team1 : team2;
    const enemyTeam = myTeam === team1 ? team2 : team1;

    const myRounds = counted.filter(r => inSide(r, me));
    const { pct: myPct } = swingOf(me);

    // Another round drops the hovered event and its tooltip: they belong to the old round's markers.
    const pickRound = (n: number | null) => {
        setRoundNo(n);
        setHot(null);
        tip.hide();
    };

    // Default round: the one that moved this player's swing the most.
    useEffect(() => {
        const best = [...myRounds].sort((a, b) => Math.abs(creditOf(b, me)) - Math.abs(creditOf(a, me)))[0];
        pickRound(best ? best.n : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [me, side, data]);

    const ledger = useMemo(() => {
        const sums: Record<Cat, number> = { kill: 0, mateKill: 0, death: 0, mateDeath: 0, plant: 0, end: 0, other: 0 };
        for (const r of myRounds) {
            const mates = (sideOf(r, me) === 'ct' ? r.ct : r.t).filter(i => i !== me);
            for (const e of r.events) sums[categorize(e, me, mates)] += deltaOf(e, me);
        }
        return sums;
    }, [myRounds, me]);
    const ledgerMax = Math.max(1, ...CAT_ORDER.map(c => Math.abs(ledger[c])));
    const total = myRounds.reduce((a, r) => a + creditOf(r, me), 0);

    const roundIdx = myRounds.findIndex(r => r.n === roundNo);
    const round = roundIdx >= 0 ? myRounds[roundIdx] : null;
    const step = (d: number) => {
        const next = myRounds[roundIdx + d];
        if (next) pickRound(next.n);
    };
    const onKey = (e: React.KeyboardEvent) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        if (/^(INPUT|TEXTAREA|SELECT)$/.test((e.target as HTMLElement).tagName)) return;
        e.preventDefault();
        step(e.key === 'ArrowLeft' ? -1 : 1);
    };

    const copyLink = () => {
        const u = new URL(window.location.href);
        u.searchParams.set('swing', '1');
        const done = () => { setCopied(true); window.setTimeout(() => setCopied(false), 1800); };
        navigator.clipboard?.writeText(u.toString()).then(done).catch(() => window.prompt?.('', u.toString()));
    };

    const econ = (s: string) => t(`match2.swing.econ.${s}`, { defaultValue: s });
    const reasonOf = (r: SwingRound) => endReasonOf(r, roundEnds);
    const score = (n: number) => {
        const [a, b] = scoreAfter.get(n) ?? [0, 0];
        return (
            <span className={styles.swingScore}>
                <TeamLogo logo={team1.teamLogo} name={team1.teamName} className={styles.swingTipLogo} />
                {a} : {b}
                <TeamLogo logo={team2.teamLogo} name={team2.teamName} className={styles.swingTipLogo} />
            </span>
        );
    };

    const evPlain = (e: SwingEvent, mySide: SideKey): string => {
        if (e.type === 'P') return t('match2.swing.evPlant');
        if (e.type.startsWith('E')) {
            const winner = (e.type[1] === 'C' ? 'ct' : 't') === mySide ? myTeam : enemyTeam;
            return t('match2.swing.evEnd', { team: winner.teamName });
        }
        if (e.type === 'K') return `${name(e.k)} ${t('match2.swing.kills')} ${name(e.vi)}`;
        if (e.type === 'TK') return `${name(e.k)} ${t('match2.swing.teamKills')} ${name(e.vi)}`;
        return `${name(e.vi)} ${t('match2.swing.dies')}`;
    };
    const evText = (e: SwingEvent, mySide: SideKey): React.ReactNode => {
        const nm = (ix?: number) => <b className={ix === me ? styles.swingMe : styles.swingName}>{name(ix)}</b>;
        if (e.type.startsWith('E')) {
            return <>
                {evPlain(e, mySide)}
                {e.type.includes('d') && ` · ${t('match2.swing.defused')}`}
                {e.type.includes('c') && ` · ${t('match2.swing.decided')}`}
            </>;
        }
        if (e.type === 'K') return <>{nm(e.k)} {t('match2.swing.kills')} {nm(e.vi)}</>;
        if (e.type === 'TK') return <>{nm(e.k)} {t('match2.swing.teamKills')} {nm(e.vi)}</>;
        if (e.type === 'P') return evPlain(e, mySide);
        return <>{nm(e.vi)} {t('match2.swing.dies')}</>;
    };

    const barTip = (r: SwingRound) => {
        const v = creditOf(r, me);
        return <>
            <div className={styles.swingTipTitle}>{t('match2.round')} {r.n}</div>
            <div className={styles.swingTipRow}>{score(r.n)}</div>
            <div className={styles.swingTipRow}>{name(me)} <b className={tone(v)}>{fmtPct(v)}</b></div>
            <div className={styles.swingTipMeta}>{t('match2.swing.barTip')}</div>
        </>;
    };
    const barLabel = (r: SwingRound) => `${t('match2.round')} ${r.n}: ${fmtPct(creditOf(r, me))}`;

    const detail = round && (() => {
        const mySide = sideOf(round, me) ?? 'ct';
        const enemySide = otherSide(mySide);
        const won = round.ctWon === (mySide === 'ct');
        const reason = reasonOf(round);
        const myRoster = mySide === 'ct' ? round.ct : round.t;
        const buys = `${mySide.toUpperCase()} ${econ(mySide === 'ct' ? round.econCt : round.econT)} · ${enemySide.toUpperCase()} ${econ(mySide === 'ct' ? round.econT : round.econCt)}`;
        // players alive [mine, theirs]: index 0 before the first event, j + 1 after event j
        const alive: [number, number][] = [[Math.min(myRoster.length, 5), Math.min((mySide === 'ct' ? round.t : round.ct).length, 5)]];
        for (const e of round.events) {
            const [a, b] = alive[alive.length - 1];
            const mineDied = isDeath(e) && e.vi != null && myRoster.includes(e.vi);
            const theirsDied = isDeath(e) && e.vi != null && !myRoster.includes(e.vi);
            alive.push([Math.max(a - Number(mineDied), 0), Math.max(b - Number(theirsDied), 0)]);
        }

        const eventTip = (j: number) => {
            const e = j >= 0 ? round.events[j] : null;
            const mine = mySide === 'ct' ? 100 - (e ? e.wta : round.events[0].wtb) : (e ? e.wta : round.events[0].wtb);
            const [a, b] = alive[j + 1];
            const v = e ? deltaOf(e, me) : 0;
            const others = e
                ? e.d.filter(([ix, dl]) => ix !== me && Math.abs(dl) >= 0.05).sort((x, y) => Math.abs(y[1]) - Math.abs(x[1])).slice(0, 3)
                : [];
            return <>
                <div className={styles.swingTipOdds}>
                    <TeamLogo logo={myTeam.teamLogo} name={myTeam.teamName} className={styles.swingTipLogo} />
                    <span style={{ color: sideColor(mySide) }}>{mine.toFixed(1)}%</span>
                    <span className={styles.swingTipAlive}>{e?.type.startsWith('E') ? '' : `${a}v${b}`}</span>
                    <span style={{ color: sideColor(enemySide) }}>{(100 - mine).toFixed(1)}%</span>
                    <TeamLogo logo={enemyTeam.teamLogo} name={enemyTeam.teamName} className={styles.swingTipLogo} />
                </div>
                <div className={styles.swingTipEvent}>{e ? evText(e, mySide) : t('match2.swing.roundStart')}</div>
                <div className={styles.swingTipMeta}>
                    {e ? (e.s != null ? `${e.s.toFixed(1)}s` : '') : buys}
                    {e?.frozen && ` · ${t('match2.swing.frozen')}`}
                </div>
                {e && (Math.abs(v) >= 0.05 || e.k === me || e.vi === me) && (
                    <div className={styles.swingTipMe}>{name(me)} <b className={tone(v)}>{fmtPct(v)}</b></div>
                )}
                {others.length > 0 && (
                    <div className={styles.swingTipMeta}>
                        {others.map(([ix, dl], k) => (
                            <span key={ix}>{k > 0 && ', '}{name(ix)} <span className={tone(dl)}>{fmtPct(dl)}</span></span>
                        ))}
                    </div>
                )}
            </>;
        };
        const eventLabel = (j: number) => (j < 0 ? `${t('match2.swing.roundStart')}, ${buys}` : evPlain(round.events[j], mySide));

        return <>
            <div className={styles.swingRdHead}>
                <span className={styles.swingRdTitle}>{t('match2.round')} {round.n}</span>
                {score(round.n)}
                <span className={styles.swingMuted}>
                    {buys} · {won ? t('match2.swing.won', { team: myTeam.teamName }) : t('match2.swing.lost', { team: myTeam.teamName })}
                </span>
                <span className={`${styles.swingRoundTotal} ${tone(creditOf(round, me))}`}>
                    {name(me)} {fmtPct(creditOf(round, me))}
                </span>
                <span className={styles.swingRdNav}>
                    <button type="button" className={styles.swingRdNavBtn} onClick={() => step(-1)} disabled={roundIdx <= 0}
                        aria-label={t('match2.swing.prevRound')} title={t('match2.swing.prevRound')}>
                        <ChevronLeft sx={{ fontSize: 18 }} />
                    </button>
                    <button type="button" className={styles.swingRdNavBtn} onClick={() => step(1)} disabled={roundIdx >= myRounds.length - 1}
                        aria-label={t('match2.swing.nextRound')} title={t('match2.swing.nextRound')}>
                        <ChevronRight sx={{ fontSize: 18 }} />
                    </button>
                </span>
            </div>
            <div className={styles.swingBody}>
                <div className={styles.swingRcLegend}>
                    <span><i className={styles.swingRcSwatch} style={{ background: sideColor(mySide) }} />{myTeam.teamName} · {mySide.toUpperCase()}</span>
                    <span><i className={styles.swingRcSwatch} style={{ background: sideColor(enemySide) }} />{enemyTeam.teamName} · {enemySide.toUpperCase()}</span>
                    <span className={styles.swingRcLegendNote}>{t('match2.swing.chartLegend')}</span>
                </div>
                <RoundChart round={round} me={me} mySide={mySide} reason={reason} hot={hot} setHot={setHot} tip={tip}
                    tipFor={eventTip} labelFor={eventLabel} decidedLabel={t('match2.swing.decidedMarker')} />
                <div className={styles.swingTableWrap}>
                    <table className={styles.swingTable}>
                        <thead>
                            <tr>
                                <th className={styles.swingNum}>{t('match2.swing.colTime')}</th>
                                <th>{t('match2.swing.colEvent')}</th>
                                <th className={styles.swingNum}>{t('match2.swing.colAlive')}</th>
                                <th className={styles.swingNum}>{t('match2.swing.colChance')}</th>
                                <th className={styles.swingNum}>{name(me)}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {round.events.map((e, j) => {
                                const v = deltaOf(e, me);
                                const involved = e.k === me || e.vi === me || Math.abs(v) >= 0.05;
                                const others = e.d
                                    .filter(([ix, dl]) => ix !== me && Math.abs(dl) >= 0.05)
                                    .sort((a, b) => b[1] - a[1]);
                                const mk = markOf(round, e, reason);
                                const wp = (wt: number) => (mySide === 'ct' ? 100 - wt : wt);
                                const cls = [involved ? styles.swingRowMe : '', e.frozen ? styles.swingRowFrozen : '', hot === j ? styles.swingRowHot : ''].join(' ');
                                return (
                                    <tr key={j} className={cls} onMouseEnter={() => setHot(j)} onMouseLeave={() => setHot(null)}>
                                        <td className={styles.swingNum}>{e.s != null ? `${e.s.toFixed(1)}s` : ''}</td>
                                        <td>
                                            <span className={styles.swingEvIcon} style={maskStyle(mk.icon, mk.color)} />
                                            {evText(e, mySide)}
                                            {e.frozen && <span className={styles.swingNote}>{t('match2.swing.frozen')}</span>}
                                            {others.length > 0 && (
                                                <span className={styles.swingNote}>
                                                    {others.map(([ix, dl], k) => (
                                                        <span key={ix}>{k > 0 && ', '}{name(ix)} <span className={tone(dl)}>{fmtPct(dl)}</span></span>
                                                    ))}
                                                </span>
                                            )}
                                        </td>
                                        <td className={styles.swingNum}>{e.type.startsWith('E') ? '' : `${alive[j + 1][0]}v${alive[j + 1][1]}`}</td>
                                        <td className={styles.swingNum}>{wp(e.wtb).toFixed(1)}% → {wp(e.wta).toFixed(1)}%</td>
                                        <td className={`${styles.swingNum} ${tone(v)}`}><b>{Math.abs(v) >= 0.05 ? fmtPct(v) : '0.0%'}</b></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </>;
    })();

    return (
        <div className={styles.fadeIn} style={{ marginTop: 18 }}>
            {/* Player picker: one segmented row per team in the header's order, built like the T/CT toggle.
                Roster order, so the keys don't move when the side filter changes the numbers. */}
            <div className={styles.swingPicker}>
                {[team1, team2].map(team => {
                    const players = order.filter(ix => t1.has(ix) === (team === team1));
                    return (
                        <div key={team.teamId ?? team.teamName} className={styles.swingPickerRow}>
                            <div className={styles.swingPickerTeam}>
                                <TeamLogo logo={team.teamLogo} name={team.teamName} className={styles.swingPickerLogo} />
                                <span>{team.teamName}</span>
                            </div>
                            <div className={styles.swingPickerSeg} style={{ '--n': players.length } as React.CSSProperties}>
                                {players.map(ix => {
                                    const { pct } = swingOf(ix);
                                    return (
                                        <button key={data.players[ix].id} type="button" aria-pressed={ix === me} title={name(ix)}
                                            className={`${styles.swingPickerBtn} ${ix === me ? styles.swingPickerBtnActive : ''}`}
                                            onClick={() => onSelect(data.players[ix].id)}>
                                            <span className={styles.swingPickerName}>{name(ix)}</span>
                                            <span className={`${styles.swingPickerVal} ${pct != null ? tone(pct) : styles.swingMuted}`}>
                                                {pct != null ? fmtPct(pct, 2) : '–'}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className={styles.board} onKeyDown={onKey}>
                <div className={styles.swingHead}>
                    <div>
                        <div className={styles.sectionLabel}>
                            {t('match2.swing.title')}
                        </div>
                        <div className={styles.swingWho}>
                            {name(me)}
                            {side !== 'all' && <span className={styles.swingSide}>{side.toUpperCase()}</span>}
                        </div>
                    </div>
                    <div className={styles.swingHeadRight}>
                        <div className={`${styles.cardBigValue} ${myPct != null ? tone(myPct) : ''}`}>
                            {myPct != null ? fmtPct(myPct, 2) : '–'}
                        </div>
                        <div className={styles.swingMuted}>
                            {t('match2.swing.perRound', { total: fmtPct(total), rounds: myRounds.length })}
                        </div>
                    </div>
                </div>
                <div className={styles.swingBody}>
                    <div className={styles.sectionLabel}>{t('match2.swing.byRound')}</div>
                    <RoundBars rounds={data.rounds} me={me} isOn={r => !r.skipped && inSide(r, me)} selected={roundNo}
                        onPick={pickRound} reasonOf={reasonOf} tipFor={barTip} labelFor={barLabel} tip={tip} />
                </div>
                {detail}
            </div>

            <div className={styles.board}>
                <div className={styles.boardHeader}>
                    <span className={styles.boardTeamName}>{t('match2.swing.sourceTitle')}</span>
                </div>
                <div className={styles.swingBody}>
                    <p className={styles.swingIntro}>{t('match2.swing.intro')}</p>
                    <div className={styles.swingLedger}>
                        {CAT_ORDER.filter(c => Math.abs(ledger[c]) >= 0.05).map(c => {
                            const v = ledger[c];
                            const w = (Math.abs(v) / ledgerMax) * 50;
                            return (
                                <div key={c} className={styles.swingLedgerRow}>
                                    <span>{t(`match2.swing.cat.${c}`)}</span>
                                    <span className={styles.swingTrack}>
                                        <span className={`${styles.swingBar} ${v >= 0 ? styles.swingBarPos : styles.swingBarNeg}`}
                                            style={{ width: `${w}%` }} />
                                    </span>
                                    <span className={`${styles.swingLedgerVal} ${tone(v)}`}>{fmtPct(v)}</span>
                                </div>
                            );
                        })}
                    </div>
                    <div>
                        <button className={styles.swingCopy} onClick={copyLink}>
                            {copied ? <Check sx={{ fontSize: 15 }} /> : <ContentCopy sx={{ fontSize: 15 }} />}
                            {copied ? t('match2.swing.copied') : t('match2.swing.copyLink')}
                        </button>
                    </div>
                </div>
            </div>
            {tip.node}
        </div>
    );
};

export default Match2Swing;
